// Consolidated utilities for Hotspot components

/**
 * Tooltip positioning and data utilities
 */
export const initTooltipDirection = (data, viewData) => {
  const viewLimits = {
    leftLimit: viewData.x_offset,
    rightLimit: viewData.x_offset + viewData.size.width,
    topLimit: viewData.y_offset,
    bottomLimit: viewData.y_offset + viewData.size.height,
  };
  const hotspotLimits = {
    leftLimit: data.x_offset,
    rightLimit: data.x_offset + data.width,
    topLimit: data.y_offset,
    bottomLimit: data.y_offset + data.height,
  };

  const topDiff = hotspotLimits.topLimit - viewLimits.topLimit;
  const bottomDiff = viewLimits.bottomLimit - hotspotLimits.bottomLimit;
  const vert = topDiff > bottomDiff ? "top" : "bottom";
  const leftDiff = hotspotLimits.leftLimit - viewLimits.leftLimit;
  const rightDiff = viewLimits.rightLimit - hotspotLimits.rightLimit;
  const horr = leftDiff > rightDiff ? "left" : "right";

  return `${vert}-${horr}`;
};

export const getTooltipData = (data) => {
  const isDropdownType =
    data?.props?.type === "state-changer" &&
    data?.props?.stateChangerType === "Dropdown";
  const opts = data?.props?.options ?? [];
  const currState1 = data?.props?.currentState ?? 1; // 1-based
  const currIndex0 = Math.max(0, currState1 - 1);
  const currOpt = isDropdownType ? opts[currIndex0] : undefined;
  const isCorrectVariant = !!currOpt && currOpt.correctness === "Correct";
  const isIncorrectVariant = !!currOpt && currOpt.correctness === "Incorrect";
  const derivedTooltipText =
    isIncorrectVariant || isCorrectVariant
      ? (currOpt?.name ?? "")
      : (data?.tooltipText ?? "");

  return {
    isCorrectVariant,
    isIncorrectVariant,
    derivedTooltipText,
    currOpt,
  };
};

/**
 * Text body size calculation utilities
 */
export const checkTextBodySize = (
  id,
  data,
  isDiabledBlurOnSmall,
  setTextBodyWidth,
  setTextBodyHeight,
  setTextBodyIsSmall
) => {
  const elem = document.getElementById(`textBody${id}`);
  if (elem !== null) {
    const width = elem.offsetWidth;
    const height = elem.offsetHeight;
    setTextBodyWidth(width);
    setTextBodyHeight(height);

    const minPixPerCell = 25;
    const addPixPerCell = 50;
    const pixGrowthPerCell = 1.5;
    const maxThreshhold = 300;

    const threshhold =
      data.width * minPixPerCell +
      addPixPerCell * ((data.width - 1) * pixGrowthPerCell);

    if (
      ((width < threshhold && width < maxThreshhold) || height < 55) &&
      !isDiabledBlurOnSmall
    ) {
      setTextBodyIsSmall(true);
    } else {
      setTextBodyIsSmall(false);
    }
  }
};

/**
 * Click handler creation utility
 */
export const createClickHandler = (props) => {
  const getCoordsData = () => {
    const elem = document.getElementById(`hotspotBtn_${props.id}`);
    const coords = elem.getBoundingClientRect();
    return coords;
  };

  return () => {
    switch (props.data.props.type) {
      case "view-changer":
        // Store hotspot ID mapped to current view (source view) for focus restoration
        // When returning to this view, we'll focus this hotspot
        if (props.setViewChangerHotspotId && props.currentView) {
          props.setViewChangerHotspotId(props.currentView, props.id);
        }

        props.changeView(props.data.props.setView);
        if (!props.data.triggered && props.data.postTrigger !== "no-change") {
          props.handleTrigger(props.id);
        }
        break;
      case "state-changer":
        if (
          !props.data.props.stateChangerType ||
          props.data.props.stateChangerType === "Cycle (default)"
        ) {
          props.changeState(props.id);
          if (!props.data.triggered && props.data.postTrigger !== "no-change") {
            props.handleTrigger(props.id);
          }
        }
        break;
      case "content-box":
        if (!props.data.triggered && props.data.postTrigger !== "no-change") {
          props.handleTrigger(props.id);
        }
        break;
      case "text-body":
        props.updateWidgetData(
          true,
          props.data.props.type,
          props.id,
          getCoordsData()
        );
        if (!props.data.triggered && props.data.postTrigger !== "no-change") {
          props.handleTrigger(props.id);
        }
        break;
      case "CSCQ":
        props.updateWidgetData(
          true,
          props.data.props.type,
          props.id,
          getCoordsData()
        );
        break;
      case "TABLE_QUESTION":
        props.updateWidgetData(
          true,
          props.data.props.type,
          props.id,
          getCoordsData()
        );
        break;
      case "MSCQ":
        props.updateWidgetData(
          true,
          props.data.props.type,
          props.id,
          getCoordsData()
        );
        break;
      case "learn-more":
        props.updateWidgetData(
          true,
          props.data.props.type,
          props.id,
          getCoordsData()
        );
        break;
      case "slider":
        props.updateWidgetData(
          true,
          props.data.props.type,
          props.id,
          getCoordsData()
        );
        break;
      case "none":
        props.handleTrigger(props.id);
        if (props.data.showUsersnapFeedback) {
          props.loadUsersnap().then((usersnapApi) => {
            if (usersnapApi) {
              usersnapApi
                .show(props.usersnapProjectId)
                .then((widgetApi) => widgetApi.open());
            } else {
              console.warn("Usersnap API not available");
            }
          });
        }
        break;
      case "animation-control":
        // Animation control button - toggle pause/play state
        props.handleTrigger(props.id);
        break;
      default:
        null;
    }
  };
};

/**
 * Hotspot type detection utilities
 */
export const isDropdownHotspot = (data) => {
  return (
    data.props.type === "state-changer" &&
    data.props.stateChangerType === "Dropdown"
  );
};

export const isContentBoxHotspot = (data) => {
  return data.props.type === "content-box";
};

export const isTextBodyHotspot = (data) => {
  return data.props.type === "text-body";
};

/**
 * Common hotspot style calculations
 */
export const getHotspotStyles = (data, cellSize) => {
  if (data.props.type === "content-box") {
    return {};
  }

  return {
    maxHeight: `clamp(24px, ${(38 / 100) * cellSize}px, ${cellSize}px)`,
    minHeight: `clamp(24px, 40px, ${cellSize}px)`,
    maxWidth: `clamp(24px, ${(38 / 100) * cellSize}px, ${cellSize}px)`,
    minWidth: `clamp(24px, 40px, ${cellSize}px)`,
    borderWidth: `clamp(1px, ${0.025 * cellSize}px, ${0.025 * cellSize}px)`,
  };
};

/**
 * Common hotspot class name generation
 */
export const getHotspotClassNames = (props, className) => {
  const { data, id, selectedHotspot, locked, hide, showGrid, idle } = props;

  return [
    className.hotspotContainer,
    data.props.type === "content-box"
      ? className.hotspotContainerContentBox
      : "",
    data.props.type === "animation-control"
      ? className.hotspotContainerAnimationControl
      : "",
    props.isCorrect === false
      ? className.hotspotContainerContentBoxIncorrect
      : "",
    props.isCorrect === true ? className.hotspotContainerContentBoxCorrect : "",
    id === selectedHotspot ? className.selected : "",
    data.triggered ? getPostTriggerClassName(data.postTrigger, className) : "",
    locked && !data.triggered ? className.locked : "",
    hide ? className.showHover : "",
    !showGrid ? className.showHoverOverride : "",
    hide && idle ? className.idle : "",
    ["Next", "Back", "Down", "Up"].includes(data.visualOverride)
      ? className.goldNav
      : "",
    data.visualOverride === "Orange" ? className.orange : "",
  ]
    .filter(Boolean)
    .join(" ");
};

const getPostTriggerClassName = (postTrigger, className) => {
  const triggerMap = {
    "no-change": className.noChange,
    inactive: className.inactive,
    triggered: className.triggered,
    hidden: className.hidden,
  };
  return triggerMap[postTrigger] || "";
};

/**
 * Helper to check if all dependencies of an animation state are satisfied
 * @param {object} state - Animation state with state_dependencies
 * @param {number} currentView - Current active view
 * @param {object} allHotspots - All hotspots with their current states
 * @param {array} activeTriggers - Currently active custom triggers
 * @param {number|null} targetView - Target view (for view-changer checks)
 * @param {string|null} triggeringHotspotId - ID of hotspot being triggered
 * @returns {boolean} - True if all dependencies are satisfied
 */
const areAllDependenciesSatisfied = (
  state,
  currentView,
  allHotspots,
  activeTriggers,
  targetView = null,
  triggeringHotspotId = null
) => {
  const deps = state.state_dependencies || {};

  // Check view dependencies
  const viewDeps = deps.views || [];
  if (viewDeps.length > 0) {
    const viewToCheck = targetView !== null ? targetView : currentView;
    const viewSatisfied = viewDeps.some(
      (v) => Number(v) === Number(viewToCheck)
    );
    if (!viewSatisfied) return false;
  }

  // Check hotspot dependencies
  const hotspotDeps = deps.hotspots || [];
  const allHotspotsSatisfied = hotspotDeps.every((dep) => {
    const depHotspotId = String(dep.id);
    const requiredTriggered = dep.requirement?.triggered;

    // If this is the hotspot we're about to trigger, check what state it needs
    if (
      triggeringHotspotId !== null &&
      depHotspotId === String(triggeringHotspotId)
    ) {
      // After clicking, this hotspot will be triggered
      return requiredTriggered === true;
    }

    // Check current state of other hotspots
    const hotspot = allHotspots[depHotspotId];
    if (!hotspot) return false;

    return hotspot.triggered === requiredTriggered;
  });
  if (!allHotspotsSatisfied) return false;

  // Check custom trigger dependencies
  const customTriggerDeps = deps.customTriggers || [];
  const allCustomTriggersSatisfied = customTriggerDeps.every((trigger) =>
    activeTriggers.includes(trigger)
  );
  if (!allCustomTriggersSatisfied) return false;

  return true;
};

/**
 * Check if clicking this hotspot will trigger an animation to play
 * This is a comprehensive check that verifies ALL dependencies will be satisfied
 *
 * @param {string} hotspotId - The ID of the hotspot being checked
 * @param {object} hotspotData - The hotspot's data
 * @param {array} entities - All entities in the scene
 * @param {number} currentView - The current active view
 * @param {object} allHotspots - All hotspots in the scene with their current states
 * @param {array} activeTriggers - Currently active custom triggers
 * @returns {boolean} - True if clicking this hotspot will actually trigger an animation
 */
export const willTriggerAnimation = (
  hotspotId,
  hotspotData,
  entities,
  currentView,
  allHotspots,
  activeTriggers = []
) => {
  if (!entities || !Array.isArray(entities)) {
    return false;
  }

  const hotspotType = hotspotData?.props?.type;

  // Check view-changer hotspots
  if (hotspotType === "view-changer") {
    const targetView = hotspotData.props.setView;

    // Check if any animation will play in the target view
    return entities.some((entity) => {
      if (entity.type !== "animation") return false;

      return entity.states?.some((state) => {
        const frameRange = state.frameRange || {};

        // Must have actual movement
        if (frameRange.start === frameRange.end) return false;

        // Check if all dependencies will be satisfied in target view
        return areAllDependenciesSatisfied(
          state,
          currentView,
          allHotspots,
          activeTriggers,
          targetView,
          null
        );
      });
    });
  }

  // Check other hotspots - does triggering this hotspot satisfy an animation's requirements?
  return entities.some((entity) => {
    if (entity.type !== "animation") return false;

    return entity.states?.some((state) => {
      const hotspotDeps = state.state_dependencies?.hotspots || [];
      const frameRange = state.frameRange || {};

      // Must have actual movement
      if (frameRange.start === frameRange.end) return false;

      // Check if this hotspot is required for this animation
      const isRequired = hotspotDeps.some((dep) => {
        return (
          String(dep.id) === String(hotspotId) &&
          dep.requirement?.triggered === true
        );
      });

      if (!isRequired) return false;

      // Check if all dependencies will be satisfied after triggering this hotspot
      return areAllDependenciesSatisfied(
        state,
        currentView,
        allHotspots,
        activeTriggers,
        null,
        hotspotId
      );
    });
  });
};

/**
 * Get animation alt texts that will be triggered when clicking this hotspot
 * @param {string} hotspotId - The ID of the hotspot being checked
 * @param {object} hotspotData - The hotspot's data
 * @param {array} entities - All entities in the scene
 * @param {number} currentView - The current active view
 * @param {object} allHotspots - All hotspots in the scene with their current states
 * @param {array} activeTriggers - Currently active custom triggers
 * @returns {array} - Array of alt text strings from animations that will play
 */
export const getAnimationAltTexts = (
  hotspotId,
  hotspotData,
  entities,
  currentView,
  allHotspots,
  activeTriggers = []
) => {
  if (!entities || !Array.isArray(entities)) {
    return [];
  }

  const hotspotType = hotspotData?.props?.type;
  const altTexts = [];

  // Check view-changer hotspots
  if (hotspotType === "view-changer") {
    const targetView = hotspotData.props.setView;

    entities.forEach((entity) => {
      if (entity.type !== "animation") return;

      entity.states?.forEach((state) => {
        const frameRange = state.frameRange || {};

        // Must have actual movement
        if (frameRange.start === frameRange.end) return;

        // Check if all dependencies will be satisfied in target view
        if (
          areAllDependenciesSatisfied(
            state,
            currentView,
            allHotspots,
            activeTriggers,
            targetView,
            null
          )
        ) {
          // Only include if animation has alt text
          if (state.altText && state.altText.trim() !== "") {
            altTexts.push(state.altText);
          }
        }
      });
    });

    return altTexts;
  }

  // Check other hotspots
  entities.forEach((entity) => {
    if (entity.type !== "animation") return;

    entity.states?.forEach((state) => {
      const hotspotDeps = state.state_dependencies?.hotspots || [];
      const frameRange = state.frameRange || {};

      // Must have actual movement
      if (frameRange.start === frameRange.end) return;

      // Check if this hotspot is required for this animation
      const isRequired = hotspotDeps.some((dep) => {
        return (
          String(dep.id) === String(hotspotId) &&
          dep.requirement?.triggered === true
        );
      });

      if (!isRequired) return;

      // Check if all dependencies will be satisfied
      if (
        areAllDependenciesSatisfied(
          state,
          currentView,
          allHotspots,
          activeTriggers,
          null,
          hotspotId
        )
      ) {
        // Only include if animation has alt text
        if (state.altText && state.altText.trim() !== "") {
          altTexts.push(state.altText);
        }
      }
    });
  });

  return altTexts;
};

/**
 * Determines if "hotspot" text should be appended to aria-label
 * Returns false for navigation controls and Hand icon (self-descriptive)
 * Returns true for all other cases (Plus, Question, etc.)
 */
export const shouldAppendHotspotText = (data) => {
  const NAVIGATION_CONTROLS = ["Next", "Back", "Down", "Up", "Hand"];
  return !NAVIGATION_CONTROLS.includes(data.visualOverride);
};

/**
 * Handle Tab key navigation for triggered hotspots
 * Phase 1: Look for untriggered, non-navigation, non-text-body hotspot (with wrap-around)
 * Phase 2: If none found, look for any focusable hotspot forward (no wrap-around)
 * Phase 3: If still none, fall back to natural browser Tab behavior
 */
export const handleTabKeyNavigation = (
  e,
  hotspotId,
  hotspotData,
  hotspotOrder,
  allHotspots,
  currentView
) => {
  // Only handle Tab key on triggered hotspots
  if (e.key !== "Tab" || e.shiftKey || !hotspotData.triggered) {
    return; // Let browser handle naturally
  }

  // Don't intercept if we don't have hotspotOrder or allHotspots
  if (!hotspotOrder || !allHotspots) {
    return;
  }

  const currentIndex = hotspotOrder.indexOf(hotspotId);
  if (currentIndex === -1) return;

  // Helper to check if a hotspot is untriggered and meets all conditions
  const isUntriggeredAndFocusable = (hotspotIdToCheck) => {
    const hotspot = allHotspots[hotspotIdToCheck];
    if (!hotspot) return false;

    // Must be untriggered
    if (hotspot.triggered) return false;

    // Skip navigation hotspots (back, next, down, up)
    const navigationTypes = ["Next", "Back", "Down", "Up"];
    if (navigationTypes.includes(hotspot.visualOverride)) return false;

    // Skip text-body hotspots
    if (hotspot.props?.type === "text-body") return false;

    // Must be visible in current view
    const visibleInView =
      hotspot.visibility_dependencies.views.includes(currentView) ||
      hotspot.visibility_dependencies.views.length === 0;
    if (!visibleInView) return false;

    // Check if element exists and is focusable
    const element = document.getElementById(`hotspotBtn_${hotspotIdToCheck}`);
    if (!element || element.disabled || element.tabIndex < 0) return false;

    return true;
  };

  // Helper to check if a hotspot element is focusable (regardless of triggered state)
  const isElementFocusable = (hotspotIdToCheck) => {
    const element = document.getElementById(`hotspotBtn_${hotspotIdToCheck}`);
    if (!element || element.disabled || element.tabIndex < 0) return false;
    return true;
  };

  // Phase 1: Search for untriggered hotspots - forward from current position
  for (let i = currentIndex + 1; i < hotspotOrder.length; i++) {
    const hotspotIdToFocus = hotspotOrder[i];
    if (isUntriggeredAndFocusable(hotspotIdToFocus)) {
      const element = document.getElementById(`hotspotBtn_${hotspotIdToFocus}`);
      if (element) {
        e.preventDefault();
        element.focus();
        return;
      }
    }
  }

  // Phase 1 continued: Wrap around and search from start to current
  for (let i = 0; i < currentIndex; i++) {
    const hotspotIdToFocus = hotspotOrder[i];
    if (isUntriggeredAndFocusable(hotspotIdToFocus)) {
      const element = document.getElementById(`hotspotBtn_${hotspotIdToFocus}`);
      if (element) {
        e.preventDefault();
        element.focus();
        return;
      }
    }
  }

  // Phase 2: No untriggered hotspots found - look for ANY focusable element forward (no wrap)
  for (let i = currentIndex + 1; i < hotspotOrder.length; i++) {
    const hotspotIdToFocus = hotspotOrder[i];
    if (isElementFocusable(hotspotIdToFocus)) {
      const element = document.getElementById(`hotspotBtn_${hotspotIdToFocus}`);
      if (element) {
        e.preventDefault();
        element.focus();
        return;
      }
    }
  }

  // Phase 3: No focusable elements found - let browser handle Tab naturally
  // Do NOT call e.preventDefault() - this allows focus to move to next focusable element in DOM
};
